import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Circle, MinusCircle, RefreshCw, ShieldCheck, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { txUrl, addrUrl } from "../lib/chains";

const LABELS = { deployed: "Token deployed", verified: "Source code verified", liquidity: "Liquidity added", trading: "Trading confirmed", profile: "Profile published" };

const evidenceLink = (s, chainId) => {
  if (!s.evidence) return null;
  if (s.evidence.startsWith("http")) return s.evidence;
  return s.evidence.length === 66 ? txUrl(s.evidence, chainId) : addrUrl(s.evidence, chainId);
};

export const LaunchStatus = ({ token }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/launch/${token.chain_id}/${token.address}/status`).then((r) => setSt(r.data)).catch(() => setSt(null)), [token.chain_id, token.address]);
  useEffect(() => { load(); }, [load]);
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/launch/${token.chain_id}/${token.address}/verify`);
      data.status === "verified" ? toast.success("Source code verified on Sourcify") : toast.message("Verification submitted — still processing, re-check in a minute");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    load();
  };
  if (!st) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3" data-testid="launch-status">
      <div className="flex items-center gap-2">
        <p className="text-xs font-bold uppercase tracking-widest text-lime">{st.fully_launched ? "Fully launched" : "Launch status"}</p>
        <button data-testid="launch-status-refresh" onClick={load} className="ml-auto text-[11px] text-slate-400 hover:text-lime flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Re-check</button>
      </div>
      <ol className="space-y-2">
        {st.stages.map((s) => {
          const Icon = s.na ? MinusCircle : s.done ? CheckCircle2 : Circle;
          const link = evidenceLink(s, token.chain_id);
          return (
            <li key={s.id} data-testid={`launch-stage-${s.id}`} data-state={s.na ? "na" : s.done ? "done" : "todo"} className="flex items-start gap-2 text-xs">
              <Icon className={`h-4 w-4 shrink-0 ${s.done ? "text-lime" : "text-slate-600"}`} />
              <span><b className={s.done ? "text-white" : "text-slate-300"}>{LABELS[s.id]}</b><span className="block text-[11px] text-slate-500">{s.detail}</span></span>
              {link && <a href={link} target="_blank" rel="noreferrer" className="ml-auto text-lime hover:underline flex items-center gap-1 shrink-0">proof <ExternalLink className="h-3 w-3" /></a>}
              {s.id === "verified" && !s.done && (
                <button data-testid="launch-verify-button" disabled={busy} onClick={verify} className="ml-auto shrink-0 rounded-lg border border-lime/40 px-2 py-1 text-[11px] text-lime hover:bg-lime/10 flex items-center gap-1 disabled:opacity-50">
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />} Verify source
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};
